import { pickRandomItem, RandomPickEntryList } from '@/webgl/runner/core/Random'
import { BonusType } from '@/webgl/runner/entities/BonusEntity'
import { CollectibleData, CollectibleType } from '@/webgl/runner/entities/CollectibleEntity'
import { TrackEntity } from '@/webgl/runner/entities/TrackEntity'
import GameWorld from '@/webgl/runner/GameWorld'
import ICollectibleTrack from '@/webgl/runner/interfaces/ICollectibleTrack'

export type ProceduralTrackConfig = {
  type: 'procedural'

  /** Collectibles enabled to spawn on track */
  collectibles: string[]

  /** Spawn a barrier of collectible in place of the next objective */
  barriers?: string[]

  /** Interval between collectible spawn, low value = more collectible, higher value = less */
  rate?: number

  /** Random interval between collectible spawn */
  variation?: number

  /** Bonus item that need to be grab before spawning objective */
  mandatoryBonus?: BonusType

  /** Duration in seconds with no valid objective spawn at the begining, default is 2s */
  warmupTime?: number

  /** Duration in seconds track not spawning anything */
  idleTime?: number
}

/** Number of collectible per chunk generation */
const CHUNK_SIZE = 4

export default class ProceduralTrack implements ICollectibleTrack {
  pool: CollectibleData[] = []
  barriers: CollectibleData[] = []

  idleTime: number = 0
  warmupTime: number = 1.7

  rate: number = 2.0
  variation: number = 2.0

  barrierMax: number = 1
  barrierSpawned: number = 0
  chunkWithoutGoal: number = 0

  time: number = 0
  timeline: TrackEntity[] = []

  constructor(
    readonly world: GameWorld,
    readonly config: ProceduralTrackConfig
  ) {
    this.world = world

    this.rate = config.rate || this.rate
    this.variation = config.variation || this.variation
    this.warmupTime = config.warmupTime !== undefined ? config.warmupTime : this.warmupTime
    this.idleTime = config.idleTime !== undefined ? config.idleTime : this.idleTime
    this.config = { ...config }

    for (const id of config.collectibles) {
      this.pool.push(this.world.registry.collectibles.get(id))
    }

    if (config.barriers) {
      for (const id of config.barriers) {
        this.barriers.push(this.world.registry.collectibles.get(id))
      }
    }
  }

  clear(): void {
    this.time = 0
    this.timeline = []
    this.pool = []
    this.barriers = []
  }

  getDefaultRate(type: CollectibleType) {
    switch (type) {
      case undefined:
      case CollectibleType.Standard:
        return 0.5
      case CollectibleType.Obstacle:
        return 0.4
      case CollectibleType.Bonus:
        return 0.3
      case CollectibleType.TurningPoint:
        return 0
      default:
        return 0.5
    }
  }

  getWarmupRate(type: CollectibleType) {
    switch (type) {
      case undefined:
      case CollectibleType.Standard:
        return 0.5
      case CollectibleType.Obstacle:
        return 0
      case CollectibleType.Bonus:
        return 0
      case CollectibleType.TurningPoint:
        return 0
      default:
        return 0.5
    }
  }

  generateChunk(start: number, count: number) {
    if (this.pool.length === 0) {
      return
    }

    this.timeline = []
    let time = start

    const randomPick: RandomPickEntryList<CollectibleData> = []

    const goal = this.world.state.getCurrentGoal()
    const warmup = this.time < this.warmupTime

    const mandatoryBonus = this.config.mandatoryBonus ? this.config.mandatoryBonus : null
    let isMandatoryGuarded: boolean = false
    if (mandatoryBonus) {
      isMandatoryGuarded = !this.world.isBonusActive(this.config.mandatoryBonus)
    }

    for (const collectible of this.pool) {
      const isNextGoal = goal == collectible.id
      const isBonus = collectible.type === CollectibleType.Bonus
      const isMandatoryBonus = collectible.bonus === mandatoryBonus

      let proba: number
      if (warmup) {
        proba = this.getWarmupRate(collectible.type)
        proba = isNextGoal ? 0.0 : proba
      } else {
        const boost = Math.min(this.chunkWithoutGoal, 3)
        proba = this.getDefaultRate(collectible.type)
        proba += isNextGoal ? 0.1 * boost : 0.0
        proba *= isNextGoal && isMandatoryGuarded && !isMandatoryBonus ? 0.0 : 1.0
        proba *= isBonus && isMandatoryGuarded && !isMandatoryBonus ? 0.0 : 1.0
        proba *= !isMandatoryGuarded && isMandatoryBonus ? 0.0 : 1.0
      }

      randomPick.push([collectible, proba])
    }

    const lanesPick: RandomPickEntryList<number> = [
      [0, 1],
      [1, 1],
      [2, 1],
    ]
    let hasGoal = false

    for (let i = 0; i < count; i++) {
      const data = pickRandomItem(randomPick)
      time += this.rate + Math.random() * this.variation

      const isGoal = data.id === goal
      const maxBarrierReached = this.barrierSpawned >= this.barrierMax
      const hasBarrier = this.barriers.length > 0

      hasGoal = hasGoal || isGoal

      if (isGoal && !maxBarrierReached && hasBarrier) {
        const barrier: CollectibleData = this.barriers[0]
        this.timeline.push({ time, lane: -1, data: barrier })
        this.timeline.push({ time, lane: 0, data: barrier })
        this.timeline.push({ time, lane: 1, data: barrier })
        this.barrierSpawned += 1
        continue
      }

      const lane = pickRandomItem(lanesPick)
      lanesPick[lane][1] = Math.max(lanesPick[lane][1] - 0.5, 0.0)
      this.timeline.push({ time, lane: lane - 1, data })
    }

    if (!hasGoal) {
      this.chunkWithoutGoal += 1
    }
  }

  evaluate(deltaTime: number): TrackEntity[] {
    this.time += deltaTime
    if (this.time < this.idleTime) {
      return []
    }

    if (this.time > this.idleTime && this.idleTime != 0) {
      this.idleTime = 0
      this.time = 0
    }

    if (this.timeline.length === 0) {
      this.generateChunk(this.time, CHUNK_SIZE)
    }

    const spawnQueue = []
    for (let i = this.timeline.length - 1; i >= 0; i--) {
      if (this.timeline[i].time < this.time) {
        spawnQueue.push(this.timeline[i])
        this.timeline.splice(i, 1)
      }
    }

    return spawnQueue
  }
}
